import { FC, ReactNode } from 'react';
import { FileWarning, UploadIcon, X } from 'lucide-react';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupDropZone } from 'features/common/components/Punchup/primitives/upload/PunchupDropZone';

import styles from './PunchupFileDropZone.module.css';

export interface PunchupFileDropZoneProps {
	/** Accepted file types passed to <input accept>. E.g. ".csv,.xlsx,.xls" */
	accept?: string;
	/** Called with the selected File when the user picks one. */
	onFileSelect: (file: File) => void;
	/** When set, shows the file-info row instead of the drop zone. */
	file?: File | null;
	/** Optional extra info shown after the file name in parentheses, e.g. "15 rows". */
	fileInfo?: string;
	/** Shows a spinner inside the file-info row while parsing is in progress. */
	isProcessing?: boolean;
	/** Shows an error box below the file-info row. */
	error?: string | null;
	/** Called when the remove button is clicked. */
	onRemove?: () => void;
	/** Supported-types line shown in the upload invitation. Keep in sync with `accept`. */
	supportedTypesLabel?: ReactNode;
}

const formatBytes = (bytes: number, decimals = 2): string => {
	if (!Number.isFinite(bytes) || bytes <= 0) return '0 Bytes';
	const BYTES_PER_UNIT = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT)), sizes.length - 1);
	return `${parseFloat((bytes / Math.pow(BYTES_PER_UNIT, unitIndex)).toFixed(decimals))} ${sizes[unitIndex]}`;
};

export const PunchupFileDropZone: FC<PunchupFileDropZoneProps> = ({
	accept,
	onFileSelect,
	file = null,
	fileInfo,
	isProcessing = false,
	error = null,
	onRemove,
	supportedTypesLabel = (
		<>
			Supported file types: <strong>.csv</strong>, <strong>.xlsx</strong>, <strong>.xls</strong>.
		</>
	),
}) => {
	if (!file) {
		return (
			<PunchupDropZone accept={accept} onFileSelect={onFileSelect}>
				<div className={styles.invitation}>
					<div className={styles.uploadIconCircle}>
						<UploadIcon size={24} />
					</div>
					<p className={styles.dropZoneText}>
						Click here to upload your file.
						<br />
						{supportedTypesLabel}
					</p>
					<PunchupButton variant="outline" type="button">
						Select file
					</PunchupButton>
				</div>
			</PunchupDropZone>
		);
	}

	return (
		<div className={styles.fileBlock}>
			<div className={styles.fileInfoRow}>
				<div className={styles.fileMeta}>
					<div className={styles.fileTexts}>
						<span className={styles.fileName}>
							{file.name}
							{fileInfo && !isProcessing && !error && ` (${fileInfo})`}
						</span>
						<span className={styles.fileSize}>{formatBytes(file.size)}</span>
					</div>
					{error && <FileWarning size={16} color="var(--punchup-color-danger)" />}
					{isProcessing && <PunchupSpinner size={16} />}
				</div>
				{onRemove && (
					<button className={styles.removeFileBtn} type="button" onClick={onRemove} aria-label="Remove file">
						<X size={16} />
					</button>
				)}
			</div>

			{error && (
				<div className={styles.errorBox}>
					<p>{error}</p>
				</div>
			)}
		</div>
	);
};
